import React, { useState } from 'react';
import { DubbingJob } from '../../types/job.types';
import { SUPPORTED_LANGUAGES, JOB_STATUS_COLORS } from '../../utils/constants';
import JobDebugPanel from './JobDebugPanel';
import { auth } from '../../services/firebase';
import './JobCard.css';

interface JobCardProps {
  job: DubbingJob;
  onViewDetails: (jobId: string) => void;
  onDelete?: (jobId: string) => void;
  refreshTrigger?: number;
  onJobUpdated?: () => void;
}

const JobCard: React.FC<JobCardProps> = ({ job, onViewDetails, onDelete, refreshTrigger, onJobUpdated }) => {
  const [showDebug, setShowDebug] = useState(false);
  const [checkingCompletion, setCheckingCompletion] = useState(false);
  const getStatusColor = (status: string) => {
    return JOB_STATUS_COLORS[status as keyof typeof JOB_STATUS_COLORS] || '#666';
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    
    // Handle Firestore Timestamp objects
    if (timestamp._seconds) {
      const date = new Date(timestamp._seconds * 1000);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    }
    
    // Handle regular dates
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return '⏳';
      case 'processing':
        return '🔄';
      case 'completed':
        return '✅';
      case 'failed':
        return '❌';
      default:
        return '❓';
    }
  };

  const handleCheckCompletion = async () => {
    if (checkingCompletion) return;
    
    setCheckingCompletion(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/gcp-virtual-production-lab/us-central1/api'}/jobs/${job.id}/check-completion`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to check completion');
      }

      const result = await response.json();
      
      if (result.success) {
        if (result.data.status === 'completed') {
          alert(`🎉 Job completed! Download links are now available.`);
          // Trigger a refresh of the job list
          if (onJobUpdated) {
            onJobUpdated();
          }
        } else {
          alert(`Job is still processing. ${result.data.filesFound || 0} of ${result.data.totalExpected || 0} files found.`);
        }
      }
    } catch (error: any) {
      console.error('Error checking completion:', error);
      alert('Failed to check job completion. Please try again.');
    } finally {
      setCheckingCompletion(false);
    }
  };

  return (
    <div className="job-card">
      <div className="job-card-header">
        <div className="job-status-badge" style={{ backgroundColor: getStatusColor(job.status) }}>
          <span className="status-icon">{getStatusIcon(job.status)}</span>
          <span className="status-text">{job.status}</span>
        </div>
        <div className="job-actions">
          <button 
            className="action-button view-button"
            onClick={() => setShowDebug(!showDebug)}
            title="Toggle Debug Info"
          >
            🐛
          </button>
          <button 
            className="action-button view-button"
            onClick={() => onViewDetails(job.id)}
            title="View Details"
          >
            👁️
          </button>
          {job.status === 'processing' && (
            <button 
              className="action-button check-button"
              onClick={handleCheckCompletion}
              disabled={checkingCompletion}
              title="Check for Completion"
            >
              {checkingCompletion ? '⏳' : '🔍'}
            </button>
          )}
          {(job.status === 'pending' || job.status === 'completed' || job.status === 'failed') && onDelete && (
            <button 
              className="action-button delete-button"
              onClick={() => onDelete(job.id)}
              title={job.status === 'pending' ? 'Cancel Job' : 'Delete Job'}
            >
              🗑️
            </button>
          )}
        </div>
      </div>

      <div className="job-card-body">
        <h3 className="job-title">{job.originalVideoName}</h3>
        
        <div className="job-details">
          <div className="detail-row">
            <span className="detail-label">Source:</span>
            <span className="detail-value">
              {SUPPORTED_LANGUAGES[job.sourceLanguage] || job.sourceLanguage}
            </span>
          </div>
          
          <div className="detail-row">
            <span className="detail-label">Target:</span>
            <span className="detail-value">
              {job.targetLanguages.map(lang => 
                SUPPORTED_LANGUAGES[lang] || lang
              ).join(', ')}
            </span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Created:</span>
            <span className="detail-value">{formatDate(job.createdAt)}</span>
          </div>

          {job.completedAt && (
            <div className="detail-row">
              <span className="detail-label">Completed:</span>
              <span className="detail-value">{formatDate(job.completedAt)}</span>
            </div>
          )}

          {job.enableDiarization && (
            <div className="detail-row">
              <span className="detail-label">Features:</span>
              <span className="detail-value">Speaker Diarization</span>
            </div>
          )}
        </div>

        {job.errorMessage && (
          <div className="job-error">
            <strong>Error:</strong> {job.errorMessage}
          </div>
        )}

        {job.status === 'completed' && job.outputUrls && (
          <div className="job-outputs">
            <h4>Available Downloads:</h4>
            <div className="download-links">
              {Object.entries(job.outputUrls).map(([lang, url]) => (
                <a 
                  key={lang}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-link"
                >
                  📥 {SUPPORTED_LANGUAGES[lang] || lang}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {showDebug && (
        <JobDebugPanel jobId={job.id} refreshTrigger={refreshTrigger} />
      )}
    </div>
  );
};

export default JobCard;